import { Chip } from '@mui/material';
import { TableGridBaseColDef, TableGridRenderCellParams } from '@components/table/types/TableColumnDef.type';
import { BORTransferRequestSearchItem } from '@lio/api-types';
import { formatDateToTimezone, ShortDateFormat } from '@utils/date';

const BORTransferFieldLabels = {
  status: 'Status',
  sourceBrokerage: 'Source Brokerage',
  sourceProducer: 'Source Producer',
  targetBrokerage: 'Target Brokerage',
  targetProducer: 'Target Producer',
  pendingCount: 'Pending Policies',
  completedCount: 'Completed Policies',
  createdAt: 'Created',
  requestingUser: 'Requested By',
};

const BOR_STATUS_CHIP_BASE_SX = { minWidth: '88px' };

export function getBORStatusChipProps(status: string): {
  variant: 'filled' | 'outlined';
  sx: Record<string, string>;
} {
  switch (status) {
    case 'Pending':
      return { variant: 'filled', sx: { ...BOR_STATUS_CHIP_BASE_SX } };
    case 'Completed':
      return {
        variant: 'filled',
        sx: { ...BOR_STATUS_CHIP_BASE_SX, backgroundColor: 'gray !important', color: 'white' },
      };
    default: // Cancelled
      return { variant: 'outlined', sx: { ...BOR_STATUS_CHIP_BASE_SX, color: 'text.secondary' } };
  }
}

export function getBORTransferRequestColumns(): TableGridBaseColDef[] {
  return [
    {
      field: 'status',
      headerName: BORTransferFieldLabels.status,
      flex: 1,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => {
        const status = params.row.status as BORTransferRequestSearchItem['status'];
        return <Chip label={status} size='small' {...getBORStatusChipProps(status)} />;
      },
    },
    {
      field: 'sourceBrokerage',
      headerName: BORTransferFieldLabels.sourceBrokerage,
      flex: 2,
      sortable: false,
      valueGetter: (_value: unknown, row: BORTransferRequestSearchItem) => row.sourceBrokerage?.name ?? '-',
    },
    {
      field: 'sourceProducer',
      headerName: BORTransferFieldLabels.sourceProducer,
      flex: 2,
      sortable: false,
      valueGetter: (_value: unknown, row: BORTransferRequestSearchItem) => {
        const user = row.sourceProducer?.user;
        return user ? `${user.firstName} ${user.lastName}` : '-';
      },
    },
    {
      field: 'targetBrokerage',
      headerName: BORTransferFieldLabels.targetBrokerage,
      flex: 2,
      sortable: false,
      valueGetter: (_value: unknown, row: BORTransferRequestSearchItem) => row.targetBrokerage?.name ?? '-',
    },
    {
      field: 'targetProducer',
      headerName: BORTransferFieldLabels.targetProducer,
      flex: 2,
      sortable: false,
      valueGetter: (_value: unknown, row: BORTransferRequestSearchItem) => {
        const user = row.targetProducer?.user;
        return user ? `${user.firstName} ${user.lastName}` : '-';
      },
    },
    {
      field: 'pendingCount',
      headerName: BORTransferFieldLabels.pendingCount,
      flex: 1,
      sortable: false,
      valueGetter: (_value: unknown, row: BORTransferRequestSearchItem) => row.pendingCount,
    },
    {
      field: 'completedCount',
      headerName: BORTransferFieldLabels.completedCount,
      flex: 1.25,
      sortable: false,
      valueGetter: (_value: unknown, row: BORTransferRequestSearchItem) => row.completedCount,
    },
    {
      field: 'createdAt',
      headerName: BORTransferFieldLabels.createdAt,
      flex: 1,
      sortable: false,
      valueGetter: (_value: unknown, row: BORTransferRequestSearchItem) =>
        row.createdAt ? formatDateToTimezone(row.createdAt, ShortDateFormat) : '-',
    },
    {
      field: 'requestingUser',
      headerName: BORTransferFieldLabels.requestingUser,
      flex: 2,
      sortable: false,
      valueGetter: (_value: unknown, row: BORTransferRequestSearchItem) => {
        const user = row.requestingUser;
        return user ? `${user.firstName} ${user.lastName}` : '-';
      },
    },
  ];
}
